// ============================================================================
// Catálogo ÚNICO de países para captura de teléfonos (25-ago-2026).
// Mata la triplicación RegisterClientPage / ClientHistoryPage / GiftCardPage.
//
// CONVENCIÓN DE ALMACENAMIENTO (contacts.phone en base):
//   · MX y US → 10 dígitos LOCALES, sin lada (statu quo de toda la plataforma:
//     las llaves phone10, el kiosko y el portal de socios dependen de esto).
//   · Cualquier otro país → dígitos completos LADA+NACIONAL (ej. Brasil
//     '5511996895548'). El backend los envía intactos (normalizePhone deja pasar
//     todo lo que no sea 10 díg. o 52+10) y el rescate @lid resuelve el JID.
//     ⚠️ NUNCA mandar el nacional pelón de otro país: lib/phone.js del backend
//     recorta cualquier 11 dígitos que empiece con 1 asumiendo EE.UU. — un
//     brasileño sin lada ('11996895548') se mutila.
//
// Los países más allá de MX/US solo se ofrecen con el flag por tenant
// tenant_settings.loyalty_flags.intl_phones (currentUser.intlPhones). Límite
// conocido (F4 pendiente): para números con lada, health score / gate / replied
// no correlacionan (llave phone10) — el envío sí funciona.
// ============================================================================

export interface PhoneCountry {
  code: string;           // ISO-2 ('MX')
  prefix: string;         // para mostrar ('+52')
  cc: string;             // dígitos de la lada que se almacenan ('52')
  name: string;
  flag: string;
  nationalDigits: number; // largo del número nacional
}

export const PHONE_COUNTRIES: PhoneCountry[] = [
  { code: 'MX', prefix: '+52',   cc: '52',  name: 'México',         flag: '🇲🇽', nationalDigits: 10 },
  { code: 'US', prefix: '+1',    cc: '1',   name: 'Estados Unidos', flag: '🇺🇸', nationalDigits: 10 },
  { code: 'BR', prefix: '+55',   cc: '55',  name: 'Brasil',         flag: '🇧🇷', nationalDigits: 11 },
  { code: 'ES', prefix: '+34',   cc: '34',  name: 'España',         flag: '🇪🇸', nationalDigits: 9 },
  { code: 'CO', prefix: '+57',   cc: '57',  name: 'Colombia',       flag: '🇨🇴', nationalDigits: 10 },
  // Argentina móvil: WhatsApp exige el 9 tras la lada (549 + 10). Sin él, el JID no resuelve.
  { code: 'AR', prefix: '+54 9', cc: '549', name: 'Argentina',      flag: '🇦🇷', nationalDigits: 10 },
  { code: 'CL', prefix: '+56',   cc: '56',  name: 'Chile',          flag: '🇨🇱', nationalDigits: 9 },
  { code: 'PE', prefix: '+51',   cc: '51',  name: 'Perú',           flag: '🇵🇪', nationalDigits: 9 },
];

// Países visibles SIN el flag intl_phones (el comportamiento de siempre).
export const BASE_COUNTRY_CODES = ['MX', 'US'];

export const countriesFor = (intlPhones: boolean | undefined): PhoneCountry[] =>
  intlPhones ? PHONE_COUNTRIES : PHONE_COUNTRIES.filter((c) => BASE_COUNTRY_CODES.includes(c.code));

export const countryByCode = (code: string): PhoneCountry | undefined =>
  PHONE_COUNTRIES.find((c) => c.code === code);

/** Forma canónica que se manda a base según el país (ver convención arriba). */
export function storedPhoneFor(countryCode: string, national: string): string {
  const d = String(national || '').replace(/\D/g, '');
  const c = countryByCode(countryCode);
  if (!c || BASE_COUNTRY_CODES.includes(c.code)) return d;   // MX/US → 10 locales
  return c.cc + d;                                            // resto → lada+nacional
}

/**
 * Descompone un teléfono TAL CUAL está guardado en base → { country, cc, national }.
 * 10 dígitos = local MX/US (indistinguibles; se reporta cc '52' como siempre hizo el
 * manager — no cambia ningún comportamiento existente). Para el resto intenta calzar
 * lada+largo exacto del catálogo; si nada calza, devuelve los dígitos tal cual con
 * cc vacía (mejor mostrar algo imperfecto que mutilar — mismo criterio que el backend).
 */
export function splitStoredPhone(stored: string): { country: PhoneCountry | null; cc: string; national: string } {
  const d = String(stored || '').replace(/\D/g, '');
  if (d.length === 10) return { country: countryByCode('MX') || null, cc: '52', national: d };
  if (d.length === 11 && d.startsWith('1')) return { country: countryByCode('US') || null, cc: '1', national: d.slice(1) };
  if (d.length === 12 && d.startsWith('52')) return { country: countryByCode('MX') || null, cc: '52', national: d.slice(2) };
  if (d.length === 13 && d.startsWith('521')) return { country: countryByCode('MX') || null, cc: '52', national: d.slice(3) };
  for (const c of PHONE_COUNTRIES) {
    if (BASE_COUNTRY_CODES.includes(c.code)) continue;
    if (d.startsWith(c.cc) && d.length === c.cc.length + c.nationalDigits) {
      return { country: c, cc: c.cc, national: d.slice(c.cc.length) };
    }
  }
  return { country: null, cc: '', national: d };
}
